import type { ReactNode } from 'react'
import { createContext, use } from 'react'

export interface ClientPortalEntity {
  id: string
  name: string
  organization: {
    id: string
    name: string
    logoUrl?: string | null
  }
}

export interface ClientPortalBranding {
  logoUrl: string | null
  accentColor: string | null
  sidebarColor: string | null
  secondaryColor: string | null
  coverImageUrl: string | null
  coverImagePosition: string | null
}

/**
 * Which workspace tabs are visible in the portal sidebar. Each flag defaults
 * to true until the portal summary loads, so tabs never flash hidden.
 * Contract Usage reuses `showRetainers`.
 */
export interface ClientPortalTabVisibility {
  showNotes: boolean
  showDocuments: boolean
  showDiscussions: boolean
  showSupport: boolean
  showKnowledge: boolean
  showRetainers: boolean
  showDeployments: boolean
  showInvoices: boolean
  showExpenseCoding: boolean
  /** Benny in the portal. Client-baseline only, off until an admin opts in. */
  showAssistant: boolean
}

export const DEFAULT_CLIENT_PORTAL_TAB_VISIBILITY: ClientPortalTabVisibility = {
  showNotes: true,
  showDocuments: true,
  showDiscussions: true,
  showSupport: true,
  // Stays true pre-load like the rest (see note above): the loaded summary is
  // what actually decides, and the API rejects unshared/hidden KB regardless.
  showKnowledge: true,
  showRetainers: true,
  showDeployments: false,
  // Financial visibility defaults off pre-load: a Billing tab flashing into
  // view and vanishing is worse than appearing a beat late.
  showInvoices: false,
  showExpenseCoding: false,
  showAssistant: false,
}

interface ClientPortalLayoutContextValue {
  currentEntity: ClientPortalEntity | null
  accessibleEntities: ClientPortalEntity[]
  isLoading: boolean
  branding: ClientPortalBranding
  tabVisibility: ClientPortalTabVisibility
  /**
   * True when the viewer is an internal org member previewing the portal.
   * Use this to hide or disable write affordances — the backend will reject
   * mutations from internal viewers regardless.
   */
  isInternalViewer: boolean
  /**
   * True once the page content has scrolled down; pinned page headers
   * (PortalPageHeader) shrink to one compact line while it is set.
   */
  isHeaderCollapsed: boolean
}

const ClientPortalLayoutContext =
  createContext<ClientPortalLayoutContextValue | null>(null)

interface ClientPortalLayoutProviderProps {
  value: ClientPortalLayoutContextValue
  children: ReactNode
}

export function ClientPortalLayoutProvider({
  value,
  children,
}: ClientPortalLayoutProviderProps) {
  return (
    <ClientPortalLayoutContext.Provider value={value}>
      {children}
    </ClientPortalLayoutContext.Provider>
  )
}

export function useClientPortalLayout(): ClientPortalLayoutContextValue {
  const ctx = use(ClientPortalLayoutContext)
  if (!ctx) {
    return {
      currentEntity: null,
      accessibleEntities: [],
      isLoading: false,
      branding: {
        logoUrl: null,
        accentColor: null,
        sidebarColor: null,
        secondaryColor: null,
        coverImageUrl: null,
        coverImagePosition: null,
      },
      tabVisibility: { ...DEFAULT_CLIENT_PORTAL_TAB_VISIBILITY },
      isInternalViewer: false,
      isHeaderCollapsed: false,
    }
  }
  return ctx
}

/**
 * Lightweight hook for write-affordance gating: returns true when the current
 * viewer is an internal org member previewing the portal.
 */
export function useIsClientPortalInternalViewer(): boolean {
  return useClientPortalLayout().isInternalViewer
}
